import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchTleCatalog } from '../loader/engine/tle';
import { predictRfimsVisibility } from './visibility';
import { RFIMS_SITES, rfimsSite } from './registry';
import { SITE_PROFILES } from '../twin/site/profiles';
import { horizonElevation } from '../twin/site/terrainMath';

const utc = (ms: number) => new Date(ms).toISOString().replace('T', ' ').slice(0, 16);

/** Separate from operational contacts, measured RF coverage and receiver telemetry. */
export function RfimsVisibilityPanel({ siteId }: { siteId?: string | null }) {
  const [chosen, setChosen] = useState(RFIMS_SITES[0].id);
  const site = rfimsSite(siteId) ?? rfimsSite(chosen)!;
  const [start, setStart] = useState<number | null>(null);
  const [mountHeightM, setMountHeightM] = useState(0);
  const [terrainHorizon, setTerrainHorizon] = useState(true);
  const terrain = useQuery({
    queryKey: ['rfims', 'site-terrain', site.id],
    enabled: start !== null,
    staleTime: Infinity,
    retry: 0,
    queryFn: async () => {
      const m = await import('../twin/site/terrainSampling');
      return m.loadSiteTerrain(SITE_PROFILES[site.id]);
    },
  });
  const horizon = useQuery({
    queryKey: ['rfims', 'terrain-horizon', site.id],
    enabled: start !== null && terrainHorizon,
    staleTime: Infinity,
    retry: 0,
    queryFn: async () => {
      const m = await import('../twin/site/terrainSampling');
      return m.loadTerrainHorizon(SITE_PROFILES[site.id]);
    },
  });
  const feed = useQuery({
    queryKey: ['rfims', 'visibility-orbits'],
    enabled: start !== null,
    queryFn: ({ signal }) => fetchTleCatalog('/api/loader/celestrak?group=active&format=tle', { signal }),
    staleTime: 5 * 60_000,
    retry: 0,
  });
  const observerM = terrain.data ? terrain.data.anchorEllipsoidM + mountHeightM : null;
  const skyline = useMemo(
    () =>
      observerM !== null && terrainHorizon && horizon.data
        ? horizon.data.map((ray) => horizonElevation(ray, observerM))
        : [],
    [observerM, terrainHorizon, horizon.data],
  );
  const ready = Boolean(feed.data && terrain.data && (!terrainHorizon || horizon.data));
  const rows = useMemo(
    () =>
      start !== null && feed.data && ready
        ? predictRfimsVisibility(site, feed.data.satellites, start, {
            observerEllipsoidM: observerM!,
            horizon: skyline,
          })
        : [],
    [site, start, feed.data, ready, observerM, skyline],
  );
  const busy = feed.isFetching || terrain.isFetching || (terrainHorizon && horizon.isFetching);
  return (
    <section className="space-y-2 rounded-panel border border-hairline p-3 text-xs text-ink-2">
      <h3 className="font-semibold text-ink">Satellite visibility planning</h3>
      <p>
        GOES-18/19 and Metop-B/C are band candidates from published RFIMS programme and spacecraft references.
        Reception, polarisation compatibility and assignments at this RMS unit are unverified.
      </p>
      {!siteId && (
        <select
          aria-label="Visibility site"
          className="w-full rounded-control bg-inset p-2 text-ink"
          value={chosen}
          onChange={(e) => setChosen(e.target.value)}
        >
          {RFIMS_SITES.map((s) => (
            <option key={s.id} value={s.id}>
              {s.rmsId} · {s.name}
            </option>
          ))}
        </select>
      )}
      <label className="flex items-center justify-between gap-2">
        Mounting height above sampled ground (m)
        <input
          aria-label="Planning mounting height"
          type="number"
          min="0"
          max="100"
          step="1"
          value={mountHeightM}
          onChange={(e) => setMountHeightM(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
          className="w-16 rounded-control border border-hairline bg-inset px-2 py-1 font-mono"
        />
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={terrainHorizon}
          onChange={(e) => setTerrainHorizon(e.target.checked)}
        />
        Include regional terrain horizon
      </label>
      <button
        className="rounded-control border border-accent/40 px-2 py-1 text-accent disabled:opacity-50"
        disabled={busy}
        onClick={() => {
          setStart(Date.now());
          if (start !== null) {
            void feed.refetch();
            if (terrain.isError) void terrain.refetch();
            if (horizon.isError) void horizon.refetch();
          }
        }}
      >
        {busy ? 'Loading orbit / terrain data…' : 'Predict next 24 hours'}
      </button>
      <p className="text-[10px] text-ink-3">
        Geometric SGP4 visibility · 5° minimum elevation · 30-second samples · no atmospheric refraction.
        Ground elevation uses Cesium World Terrain in the WGS84 ellipsoid datum. Mounting height is a planning
        assumption, not an installed measurement. Buildings, trees, RF patterns and receiver state are
        excluded.
      </p>
      {terrainHorizon && (
        <p className="text-[10px] text-ink-3">
          Regional horizon: 5° azimuth spacing, 32 distances from 80 m to 50 km, terrain level 11 and Earth
          curvature. Coarse sampling can miss narrow or more distant obstructions; this is not clearance
          certification.
        </p>
      )}
      {observerM !== null && (
        <p className="font-mono text-[10px] text-ink-3">
          Observer elevation: {observerM.toFixed(1)} m WGS84 ellipsoid
          {terrainHorizon && skyline.length
            ? ` · highest sampled horizon ${Math.max(...skyline).toFixed(1)}°`
            : ''}
        </p>
      )}
      {skyline.length > 0 && (
        <svg
          viewBox="0 0 300 75"
          role="img"
          aria-label="Sampled terrain horizon by azimuth"
          className="w-full rounded-control bg-inset"
        >
          <path d="M10 55H290" stroke="#455860" />
          <polyline
            points={[...skyline, skyline[0]]
              .map((h, i) => `${10 + (i / 72) * 280},${55 - (Math.min(40, h) / 40) * 43}`)
              .join(' ')}
            fill="none"
            stroke="#8bd7c4"
            strokeWidth="1.5"
          />
          <g fill="#aab8c2" fontSize="9">
            <text x="10" y="70">
              N
            </text>
            <text x="78" y="70">
              E
            </text>
            <text x="147" y="70">
              S
            </text>
            <text x="216" y="70">
              W
            </text>
            <text x="283" y="70">
              N
            </text>
            <text x="10" y="10">
              0–40° horizon
            </text>
          </g>
        </svg>
      )}
      {feed.isError && (
        <p className="text-warning">Orbital data unavailable. No substitute windows are generated.</p>
      )}
      {(terrain.isError || (terrainHorizon && horizon.isError)) && (
        <p className="text-warning">
          Terrain data unavailable. Requested predictions are withheld; retry or disable the regional horizon
          if only that query failed.
        </p>
      )}
      {start !== null && feed.data && ready && (
        <>
          <p className="text-[10px] text-ink-3">
            {site.rmsId} · {utc(start)} to {utc(start + 86_400_000)} UTC. Source: {feed.data.source}.
          </p>
          {rows.map((row) => (
            <details key={row.candidate.id} className="rounded-control border border-hairline p-2" open>
              <summary className="cursor-pointer font-semibold text-ink">
                {row.candidate.name} ·{' '}
                {row.unavailable ? 'unavailable' : `${row.windows.length} visibility intervals`}
              </summary>
              <a
                href={row.candidate.source}
                target="_blank"
                rel="noreferrer"
                className="text-accent underline"
              >
                {row.candidate.signal}
              </a>
              {row.epoch && <p className="text-[10px] text-ink-3">Orbit epoch: {row.epoch}</p>}
              {row.unavailable ? (
                <p className="text-warning">{row.unavailable}</p>
              ) : row.windows.length ? (
                <ul className="mt-1 space-y-1 font-mono text-[10px]">
                  {row.windows.map((w) => (
                    <li key={w.startMs}>
                      {w.startMs <= start + 30_000 ? 'Already above mask' : `Rise ${utc(w.startMs)}`} →{' '}
                      {w.endMs >= start + 86_400_000 - 30_000 ? 'past window end' : `Set ${utc(w.endMs)}`} ·
                      max {w.maxElevation.toFixed(1)}°
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No interval above 5° in this window.</p>
              )}
            </details>
          ))}
        </>
      )}
      <details className="text-[10px] text-ink-3">
        <summary className="cursor-pointer">Mission evidence and limits · reviewed 24 October 2026</summary>
        <p>
          NOAA's 2020 RFIMS presentation describes L-band monitoring, not a communications relay. NOAA-18/19
          are retained only as historical RFIMS observations, not operational candidates. SARAL is excluded
          pending current signal/status evidence.
        </p>
        <p>
          Metop-B/C are listed operational by EUMETSAT. NOAA retired certain HRPT-derived products on 1
          September 2026; that notice does not establish an RFIMS receiver assignment or the spacecraft
          transmitter state.
        </p>
        <a
          className="block text-accent underline"
          href="https://www.eumetsat.int/our-satellites/metop-series"
          target="_blank"
          rel="noreferrer"
        >
          EUMETSAT mission status
        </a>
        <a
          className="block text-accent underline"
          href="https://ospo.noaa.gov/data/messages/2026/08/MSG_20260813_2000.html"
          target="_blank"
          rel="noreferrer"
        >
          NOAA product retirement notice
        </a>
      </details>
    </section>
  );
}
